import { useCallback } from "react";

import { case_detail } from "../../../../../../../api/endpoints/case";
import ApiRequestErrorHandler from "../../../../../../../global/ApiRequestErrorHandler";
import { ACTION_TYPE } from "../components/types/actionTypes";
import {
  setfetchError,
  setfetchIsLoading,
  setfetchSuccess,
  setFieldData,
  setFieldDataIsLoading,
  setFieldMetaData,
  setShouldFetchData,
  setShowAddButton,
} from "../state/actions";

export default function useAPIFieldData(caseId, actionType, accessToken, dispatch, state) {
  const createProviderObj = (results) => {
    return results.reduce((provObj, item) => {
      provObj[item.id] = {
        id: item.id,
        fieldName: actionType,
        client: {
          id: item?.on_case?.client?.pk ?? item?.on_case?.client?.id,
          name: item.on_case.client.name,
        },
        actingUser: {
          pk: item?.acting_user?.pk,
          name: item?.acting_user?.name,
        },
        label: item?.on_provider?.name,
        detail: item.action_detail,
        timeTillDueDate: item.relative_due_date,
        dueDate: item.due_date,
        clock: item.clock_state,
        status: item.status,
        createdBy: item.created_by,
        updatedAt: item.updated_at,
        updatedBy: item.updated_by,
        completedAt: item.completed_at,
        completedBy: item.completed_by,
        formUrl: item.form_url,
        provider: {
          id: item?.on_provider?.pk ?? item?.on_provider?.id,
          name: item?.on_provider?.name,
        },
      };
      return provObj;
    }, {});
  };
  const createClientLawFirmObj = (results) => {
    let clientLawfirm = {};
    for (const item of results) {
      clientLawfirm = {
        id: item.id,
        fieldName: actionType,
        actingUser: {
          pk: item?.acting_user?.pk,
          name: item?.acting_user?.name,
        },
        client: {
          id: item.on_case.client.pk,
          name: item.on_case.client.name,
        },
        ...(item?.on_firm
          ? {
              lawfirm: {
                id: item.on_firm.pk ?? item.on_firm.id,
                name: item.on_firm.name,
                phone: item.on_firm.phone,
              },
            }
          : {}),
        label:
          actionType === ACTION_TYPE.CLIENT_FOLLOW_UP
            ? item[getActionTypeKey(actionType)].client.name
            : item[getActionTypeKey(actionType)].name,
        detail: item.action_detail,
        formUrl: item.form_url,
        timeTillDueDate: item.relative_due_date,
        dueDate: item.due_date,
        clock: item.clock_state,
        status: item.status,
        createdBy: item.created_by,
        updatedAt: item.updated_at,
        updatedBy: item.updated_by,
        completedAt: item.completed_at,
        completedBy: item.completed_by,
      };
    }
    return clientLawfirm;
  };
  const getActionTypeKey = (actionType) => {
    if (actionType === ACTION_TYPE.PROVIDER_FOLLOW_UP) {
      return "on_provider";
    } else if (actionType === ACTION_TYPE.LAW_FIRM_FOLLOW_UP) {
      return "on_firm";
    } else {
      return "on_case";
    }
  };
  const fetch = useCallback(
    async (fieldOptionsUrls) => {
      return Promise.all(fieldOptionsUrls.map(async (url) => await url))
        .then((res) => res)
        .catch((error) => {
          const errArr = ApiRequestErrorHandler(error.response);
          setfetchError(dispatch, errArr);
        });
    },
    [dispatch]
  );

  const fetchData = useCallback(async () => {
    setfetchIsLoading(dispatch, true);
    setShouldFetchData(dispatch, false);

    const fieldOptionsUrls = [case_detail.getFollowUpTooltipData(accessToken)];

    if (state.ui === null) {
      //getting tooltip, info metadata
      fetch(fieldOptionsUrls)
        .then(async (results) => {
          if (results) {
            if (results[0] && Object.keys(results[0]).length > 0) {
              return Object.keys(results[0].results).forEach((item) => {
                if (actionType === results[0].results[item].key) {
                  const newUIObj = {
                    tooltips: results[0].results[item].additional_attributes,
                    isLoading: true,
                  };

                  setFieldMetaData(dispatch, {
                    fieldName: results[0].results[item].key,
                    data: newUIObj,
                  });
                }
              });
            }
          }
        })
        .catch((error) => {
          const errArr = ApiRequestErrorHandler(error.response);
          setfetchError(dispatch, errArr);
          setfetchIsLoading(dispatch, false);
        });
    }
    // getting case follow up data
    case_detail
      .getPendingFollowUpData(caseId, actionType, accessToken)
      .then((res) => {
        let fieldData = null;

        if (res.results.length === 0) {
          setShowAddButton(dispatch, true);
        }

        if (actionType !== ACTION_TYPE.PROVIDER_FOLLOW_UP) {
          if (res.results.length > 0) {
            setShowAddButton(dispatch, false);
            fieldData = createClientLawFirmObj(res.results);
          }
        }

        if (actionType === ACTION_TYPE.PROVIDER_FOLLOW_UP) {
          setShowAddButton(dispatch, true);
          if (res.results.length > 0) {
            fieldData = createProviderObj(res.results);
          }
        }

        setFieldDataIsLoading(dispatch, {
          fieldName: actionType,
          data: true,
        });
        setFieldData(dispatch, {
          fieldName: actionType,
          data: fieldData,
        });
        setFieldDataIsLoading(dispatch, {
          fieldName: actionType,
          data: false,
        });

        return res;
      })
      .then((res) => {
        setfetchSuccess(dispatch, true);
        setfetchIsLoading(dispatch, false);
        return res;
      })
      .catch((error) => {
        const errArr = ApiRequestErrorHandler(error.response);
        setfetchError(dispatch, errArr);
        setfetchIsLoading(dispatch, false);
      });
  }, [caseId, actionType, accessToken, fetch, dispatch, state.ui]);

  return [fetchData];
}
